import classNames from "classnames";
import { JitsiConference, JitsiMeet } from "lib/lib-jitsi-meet";
import { action, computed, observable } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import { ItemCallback } from "react-grid-layout";
import ResizeDetector from "react-resize-detector";

import {
    ChatRoomState,
    Configuration,
    getDefaultRemoteVideoLayoutOptions,
    getRoomVideoLayoutLSKey,
    LocalUserState,
    MediaDevicesState,
    persistSetting,
    RemoteVideoLayoutOptions,
    RoomState,
} from "../../common";
import VideoFixtureLoader, { VIDEO_FIXTURE_NAME_PREFIX } from "../data/VideoFixtureLoader";
import RemoteParticipantDropdown from "./RemoteParticipantDropdown";
import styles from "./RemoteVideoGrid.module.css";
import ResponsiveGridLayout, { GridItemRenderInfo } from "./ResponsiveGridLayout";
import rglStyles from "./ResponsiveGridLayout.module.css";

const RGLBreakpointWidths = {
    default: 2000,
    large: 1240,
    medium: 1000,
    small: 720,
    xs: 480,
};
const RGLBreakpointCols = {
    default: 6,
    large: 5,
    medium: 4,
    small: 3,
    xs: 2,
};
const RGLBreakpointColsLarge = {
    default: 4,
    large: 3,
    medium: 2,
    small: 1,
    xs: 1,
};

export interface RemoteVideoGridProps {
    jitsi: JitsiMeet;
    room: JitsiConference;
    roomState: RoomState;
    chatRoomState: ChatRoomState;

    gridSize?: "medium" | "large";

    // for choreographic performance views
    isPerformanceView?: boolean;
    localUser?: LocalUserState;
    mediaDevices?: MediaDevicesState;
}

@observer
export default class extends React.PureComponent<RemoteVideoGridProps, never> {
    public readonly displayName = "RemoteVideoGrid";

    public static defaultProps: Partial<RemoteVideoGridProps> = {
        gridSize: "medium",
    };

    @observable public width = 0;
    @observable public height = 0;
    @action public handleGridResize = (width: number, height: number) => {
        this.width = Math.round(width);
        this.height = Math.round(height);
    };

    @observable public currentBreakpoint: keyof typeof RGLBreakpointWidths = "default";
    @observable public currentNumCols: number =
        this.props.gridSize === "medium" ? RGLBreakpointCols.default : RGLBreakpointColsLarge.default;
    @computed get colWidth(): number {
        return this.width / this.currentNumCols;
    }

    // Note: this is the main thing which videoOrientation drives
    @computed get rowHeight(): number {
        return Math.floor(
            this.props.roomState.videoOrientation === "portrait"
                ? this.colWidth * Configuration.VIDEO_ASPECT_RATIO_PORTRAIT
                : this.colWidth / Configuration.VIDEO_ASPECT_RATIO_LANDSCAPE
        );
    }
    @computed get layoutLSKey(): string {
        return getRoomVideoLayoutLSKey(this.props.room.getName());
    }

    @action
    private handleBreakpointChange = (breakpoint: keyof typeof RGLBreakpointWidths, cols: number) => {
        this.currentBreakpoint = breakpoint;
        this.currentNumCols = cols;
    };

    @action
    private handleUserLayoutChange: ItemCallback = (_layout, _oldItem, newItem) => {
        const { roomState } = this.props;
        const item = roomState.videoLayout.find((l) => l.userId === newItem.i);
        if (item !== undefined) {
            item.gridWidth = newItem.w;
            item.gridHeight = newItem.h;
            item.gridX = newItem.x;
            item.gridY = newItem.y;
        }
        persistSetting(this.layoutLSKey as any, roomState.videoLayout);
    };

    private getRemoteVideoRefHandler = (videoStream: MediaStream) => (el: HTMLVideoElement) => {
        if (el != null) {
            el.srcObject = videoStream;
        }
    };

    public render() {
        return (
            <div className={classNames(styles.remoteVideoGrid)}>
                <ResizeDetector handleWidth={true} handleHeight={true} onResize={this.handleGridResize} />
                {this.renderResponsiveReactGridLayout()}
                {Configuration.USE_VIDEO_FIXTURES && (
                    <VideoFixtureLoader roomName={this.props.room.getName()} roomState={this.props.roomState} />
                )}
            </div>
        );
    }

    private renderResponsiveReactGridLayout() {
        const { gridSize } = this.props;
        return (
            <ResponsiveGridLayout
                breakpoints={RGLBreakpointWidths}
                cols={gridSize === "medium" ? RGLBreakpointCols : RGLBreakpointColsLarge}
                currentNumCols={this.currentNumCols}
                rowHeight={this.rowHeight}
                items={this.getGridItems()}
                itemRenderer={this.renderGridItem}
                onBreakpointChange={this.handleBreakpointChange}
                onResizeStop={this.handleUserLayoutChange}
                onDragStop={this.handleUserLayoutChange}
            />
        );
    }

    private getGridItems(): GridItemRenderInfo[] {
        const { room, roomState } = this.props;
        // remote tracks may not have been added if a user just joined
        // (the video layout will have been created), so we omit those users from the grid
        const items = [];

        const { isPerformanceView, localUser, mediaDevices } = this.props;
        if (isPerformanceView && mediaDevices?.videoInputTrack !== undefined) {
            // include self view
            items.push({
                ...getDefaultRemoteVideoLayoutOptions("", "self"),
                displayName: localUser?.displayName ?? "",
                videoSource: mediaDevices.videoInputTrack.stream,
            });
        }

        for (const layout of roomState.videoLayout) {
            if (roomState.tracks.has(layout.userId)) {
                const track = roomState.tracks.get(layout.userId)!.find((t) => t.getType() === "video");
                const participant = room.getParticipantById(layout.userId);
                if (track !== undefined && participant !== undefined) {
                    const displayName = roomState.displayNames.get(layout.userId) ?? participant.getDisplayName();
                    items.push({
                        ...layout,
                        displayName,
                        videoSource: track.stream,
                    });
                }
            } else if (layout.userId.startsWith(VIDEO_FIXTURE_NAME_PREFIX)) {
                // render video fixture
                const fixtureIndex = parseInt(layout.userId.split(VIDEO_FIXTURE_NAME_PREFIX)[1], 10) - 1;
                const videoSource = roomState.videoFixtures[fixtureIndex];
                items.push({ ...layout, displayName: layout.userId, videoSource });
            }
        }

        return items;
    }

    private renderGridItem = ({ userId, isPinned, displayName, videoSource, ...gridSettings }: GridItemRenderInfo) => {
        const { roomState, isPerformanceView } = this.props;
        return (
            <div
                className={classNames(rglStyles.item, `${roomState.videoOrientation}Orientation`, {
                    [rglStyles.isPinned]: isPinned,
                })}
                key={userId}
            >
                {this.renderVideo(userId, videoSource, gridSettings)}
                {!isPerformanceView && this.renderParticipantInfo(userId, displayName, isPinned)}
            </div>
        );
    };

    private renderVideo(
        userId: string,
        videoSource: MediaStream | string,
        gridSettings: Pick<RemoteVideoLayoutOptions, "gridHeight" | "gridWidth" | "gridX" | "gridY"> = {}
    ) {
        const isPortrait = this.props.roomState.videoOrientation === "portrait";
        const { gridWidth = 1 } = gridSettings;
        const style = isPortrait
            ? {
                  // center the cropped video horizontally
                  transform: `translateX(-50%) translateX(${Math.round((this.colWidth * gridWidth) / 2)}px)`,
              }
            : undefined;

        return (
            <video
                className={styles.remoteVideo}
                ref={typeof videoSource === "string" ? undefined : this.getRemoteVideoRefHandler(videoSource)}
                src={typeof videoSource === "string" ? videoSource : undefined}
                style={style}
                autoPlay={true}
                playsInline={true}
                muted={true}
                loop={userId.startsWith(VIDEO_FIXTURE_NAME_PREFIX)}
            />
        );
    }

    private renderParticipantInfo(userId: string, displayName: string, isPinned: boolean) {
        const { roomState } = this.props;
        const layout = roomState.videoLayout.find(({ userId: id }) => id === userId);

        return (
            <div
                className={classNames(styles.remoteParticipantInfo, rglStyles.infoContainer, rglStyles.notDraggable, {
                    [rglStyles.isPinned]: isPinned,
                })}
            >
                <span className={styles.remoteParticipantName}>
                    <strong>{displayName}</strong>
                </span>
                {layout === undefined ? null : (
                    <RemoteParticipantDropdown
                        {...this.props}
                        currentNumCols={this.currentNumCols}
                        userId={userId}
                        privateChats={this.props.chatRoomState.private}
                        layout={layout}
                    />
                )}
            </div>
        );
    }
}
